import React, { useState } from 'react';
import { Box, Typography, Paper, Stack, Divider, Chip, useMediaQuery } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { openOrderWindow } from '../../../../../store/actions/orders';
import { getProductType } from '../../../../../utils/utilfunction';
import DiscoveryActions from '../../StockDiscovery/components/DiscoveryActions';
import useDiscoveryRowActions from '../../StockDiscovery/hooks/useDiscoveryRowActions';

const CardHeader = styled(Box)(({ theme, side }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  gap: theme.spacing(1),
}));

const SideBadge = styled(Box)(({ theme, side }) => ({
  padding: '2px 6px',
  borderRadius: '4px',
  fontSize: '0.7rem',
  fontWeight: 800,
  backgroundColor: side === 'call' ? '#e1f5fe' : '#fff3e0',
  color: side === 'call' ? '#0288d1' : '#f57c00',
}));

const StatusChip = styled(Chip)(({ type }) => {
  let bgcolor = '#f5f5f5';
  let color = '#757575';

  if (type === 'ITM') {
    bgcolor = '#e8f5e9';
    color = '#2e7d32';
  } else if (type === 'ATM') {
    bgcolor = '#f3e5f5';
    color = '#7b1fa2';
  } else if (type === 'OTM') {
    bgcolor = '#fff3e0';
    color = '#ef6c00';
  }

  return {
    height: '18px',
    fontSize: '0.65rem',
    fontWeight: 700,
    borderRadius: '4px',
    backgroundColor: bgcolor,
    color: color,
    '& .MuiChip-label': { paddingLeft: '4px', paddingRight: '4px' },
  };
});

const OptionRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(1.5, 2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child': {
    borderBottom: 'none',
  },
}));

const OptionListCard = ({ side, data, spotPrice }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const { handleBuy, handleSell, userSettings } = useDiscoveryRowActions();

  const isResponsive = useMediaQuery(theme.breakpoints.down('lg'));

  const handleRowClick = (row, side) => {
    const instrumentData = side === 'call' ? row.ce_data : row.pe_data;
    
    if (!instrumentData || !instrumentData.token) return;

    if (isResponsive) {
      const ltp = (side === 'call' ? row.ce_ltp : row.pe_ltp) || 0;
      const displaySymbol = (instrumentData?.display_name || instrumentData?.trading_symbol) || '';
      
      const instrument = {
        token: Number(instrumentData.token),
        exchange: instrumentData.exchange || 'NFO',
        trading_symbol: instrumentData.trading_symbol || '',
        display_name: displaySymbol,
        ltp: ltp,
        tradable: true,
        price_precision: instrumentData.price_precision ?? 2,
      };
      
      handleBuy(instrument);
    } else {
      const precision = instrumentData.price_precision || 0;
      
      // Navigate to Charts/Overview
      navigate(`/overview/tdv/${instrumentData.exchange || 'NFO'}/${instrumentData.token}/${precision}`, {
        state: { defaultTab: 'chart' }
      });
    }
  };

  return (
    <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}`, overflow: 'hidden', height: '100%' }}>
      <CardHeader side={side}>
        <SideBadge side={side}>{side === 'call' ? 'CE' : 'PE'}</SideBadge>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          {side === 'call' ? 'Call Options' : 'Put Options'}
        </Typography>
      </CardHeader>

      {/* Table Headers */}
      <Box sx={{ 
        display: 'flex', 
        px: 2, 
        py: 1, 
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
        borderBottom: `1px solid ${theme.palette.divider}`
      }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: { xs: 1, sm: 0.8 }, fontWeight: 700 }}>Options</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ flex: { xs: 1, sm: 0.8 }, textAlign: 'right', fontWeight: 700 }}>
          LTP
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ flex: { xs: 1, sm: 1.4 }, textAlign: 'right', fontWeight: 700 }}>
          Change
        </Typography>
      </Box>

      <Box sx={{ position: 'relative', minHeight: data.length > 0 ? 'auto' : '200px' }}>
        {data.length > 0 ? (
          data.map((row, idx) => {
            const strike = row.strike_price;
            const expiry = row.expiry_date;
            
            // Determine ITM/ATM/OTM
            let status = 'OTM';
            const diff = Math.abs(strike - spotPrice);
            
            let strikeStep = 50;
            if (data.length > 1) {
              strikeStep = Math.abs(data[1].strike_price - data[0].strike_price) || 50;
            }

            if (diff <= (strikeStep / 2)) {
              status = 'ATM';
            } else if (side === 'call') {
              status = strike < spotPrice ? 'ITM' : 'OTM';
            } else {
              status = strike > spotPrice ? 'ITM' : 'OTM';
            }

            const ltp = (side === 'call' ? row.ce_ltp : row.pe_ltp) || 0;
            const change = (side === 'call' ? row.ce_change : row.pe_change) || 0;
            const absChange = (side === 'call' ? row.ce_abs_change : row.pe_abs_change) || 0;
            
            const rawInstrument = side === 'call' ? row.ce_data : row.pe_data;
            const displaySymbol = (rawInstrument?.display_name || rawInstrument?.trading_symbol) || '';
            
            let baseName = (displaySymbol || '').split(' ')[0] || '';
            if (baseName === 'NIFTY' && displaySymbol.includes('BANK')) baseName = 'BANK NIFTY';
            if (baseName === 'NIFTY' && displaySymbol.includes('FIN')) baseName = 'FINNIFTY';

            // Check if the specific LTP is actually loaded (non-zero ticks)
            const isDataReady = ltp !== 0;

            // Construct instrument for DiscoveryActions
            const instrument = {
              token: Number(rawInstrument?.token),
              exchange: rawInstrument?.exchange || 'NFO',
              trading_symbol: rawInstrument?.trading_symbol || '',
              display_name: displaySymbol,
              ltp: ltp,
              tradable: true,
              price_precision: 2,
            };

            return (
              <OptionRow 
                key={idx} 
                onMouseEnter={() => setHoveredIndex(idx)}
                onMouseLeave={() => setHoveredIndex(null)}
                onClick={() => handleRowClick(row, side)}
                sx={{ 
                  opacity: isDataReady ? 1 : 0.6, 
                  transition: 'opacity 0.3s', 
                  px: { xs: 1, lg: 2 },
                  position: 'relative',
                  '&:active': { backgroundColor: 'action.selected' }
                }}
              >
                {/* Buy/Sell Pill on Hover (Desktop) */}
                {hoveredIndex === idx && (
                  <DiscoveryActions 
                    instrument={instrument}
                    onBuy={handleBuy}
                    onSell={handleSell}
                    userSettings={userSettings}
                  />
                )}

                {/* Option Info */}
                <Box sx={{ flex: { xs: 1, sm: 0.8 } }}>
                  <Stack direction="column" spacing={0.5} alignItems="flex-start">
                    <Typography variant="body2" sx={{ fontSize: { xs: '0.75rem', lg: '0.85rem' }, whiteSpace: 'nowrap', display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Box component="span" sx={{ fontWeight: 800 }}>
                        {strike}
                      </Box>
                      <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: { xs: '0.7rem', lg: '0.8rem' } }}>
                        {baseName}
                      </Box>
                      {/* Badge on Desktop */}
                      <Box sx={{ display: { xs: 'none', sm: 'inline' }, ml: 0.5 }}>
                        <StatusChip label={status} type={status} />
                      </Box>
                    </Typography>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {/* Badge on Mobile */}
                      <Box sx={{ display: { xs: 'inline', sm: 'none' } }}>
                        <StatusChip label={status} type={status} />
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', fontWeight: 600 }}>
                        {expiry}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>

                {/* LTP Column */}
                <Box sx={{ flex: { xs: 1, sm: 0.8 }, textAlign: 'right' }}>
                  <Typography variant="body2" sx={{ fontWeight: 900, fontSize: { xs: '0.75rem', lg: '0.85rem' } }}>
                    {isDataReady ? `₹${Number(ltp).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
                  </Typography>
                </Box>

                {/* Price Change Column */}
                <Box sx={{ flex: { xs: 1, sm: 1.4 }, textAlign: 'right' }}>
                  {/* For Desktop */}
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem', color: change >= 0 ? 'success.main' : 'error.main', display: { xs: 'none', sm: 'block' } }}>
                    {isDataReady ? (
                      <>
                        <Box component="span">{`${absChange >= 0 ? '+' : ''}${Number(absChange).toFixed(2)}`}</Box>
                        <Box component="span" sx={{ fontSize: '0.7rem', ml: 1, fontWeight: 500 }}>{`(${Number(change).toFixed(2)}%)`}</Box>
                      </>
                    ) : '—'}
                  </Typography>
                  {/* For Mobile */}
                  <Box sx={{ display: { xs: 'flex', sm: 'none' }, flexDirection: 'column', alignItems: 'flex-end' }}>
                    <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.7rem', color: absChange >= 0 ? 'success.main' : 'error.main' }}>
                      {isDataReady ? `${absChange >= 0 ? '+' : ''}${Number(absChange).toFixed(2)}` : ''}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.7rem', color: change >= 0 ? 'success.main' : 'error.main' }}>
                      {isDataReady ? `(${Number(change).toFixed(2)}%)` : ''}
                    </Typography>
                  </Box>
                </Box>
              </OptionRow>
            );
          })
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">Fetching strikes...</Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default OptionListCard;
